import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent
} from "react";
import { ImageOff, Music2 } from "lucide-react";

import type { CurriculumVoiceTrackId } from "@/audio/curriculum-catalog";
import type { ActivityHostRenderContext } from "@/features/activity-host";
import {
  type CurriculumAudioPlayer,
  useCurriculumFeedbackAudio
} from "@/features/audio";
import type { InputMethod } from "@/game/input";
import { AudioReplayButton, CurriculumVoiceButton } from "@/ui";

import type { EnglishActivityHostBridge } from "./audio-bridge";
import type {
  EnglishChoicePlayerAction,
  EnglishChoicePlayerRenderModel
} from "./choice-adapters";
import type {
  EnglishRhythmFocusDirection,
  EnglishRhythmPlayerAction,
  EnglishRhythmPlayerRenderModel
} from "./rhythm-adapter";
import "./english-player.css";

const UNAVAILABLE_ENGLISH_AUDIO_PLAYER: CurriculumAudioPlayer = {
  activeVoiceId: null,
  isPlayingPath: () => false,
  playPath: async () => false,
  stopOwnedVoice: () => undefined
};

interface ActivityNarrationRequestOptions<TAction> {
  readonly audioKey: CurriculumVoiceTrackId;
  readonly bridge: EnglishActivityHostBridge<TAction>;
  readonly caption: string;
  readonly createEndedAction: (playbackRequest: number) => TAction;
  readonly createUnavailableAction: (playbackRequest: number) => TAction;
  readonly inputMethod: InputMethod | null;
  readonly paused: boolean;
  readonly playbackRequest: number;
  readonly shouldPlay: boolean;
}

function useActivityNarrationRequest<TAction>({
  audioKey,
  bridge,
  caption,
  createEndedAction,
  createUnavailableAction,
  inputMethod,
  paused,
  playbackRequest,
  shouldPlay
}: ActivityNarrationRequestOptions<TAction>): void {
  useEffect(() => {
    if (!shouldPlay || paused || inputMethod === null) {
      return;
    }

    bridge.emit("narration", {
      type: "play-activity-narration",
      audioKey,
      caption,
      playbackRequest,
      endedEvent: {
        action: createEndedAction(playbackRequest),
        inputMethod
      },
      unavailableEvent: {
        action: createUnavailableAction(playbackRequest),
        inputMethod
      }
    });

    return () => {
      bridge.emit("narration", {
        type: "stop-activity-narration",
        audioKey,
        playbackRequest
      });
    };
  }, [
    audioKey,
    bridge,
    caption,
    createEndedAction,
    createUnavailableAction,
    inputMethod,
    paused,
    playbackRequest,
    shouldPlay
  ]);
}

function createChoiceEndedAction(
  playbackRequest: number
): EnglishChoicePlayerAction {
  return { type: "media-ended", playbackRequest };
}

function createChoiceUnavailableAction(
  playbackRequest: number
): EnglishChoicePlayerAction {
  return { type: "media-unavailable", playbackRequest };
}

function createRhythmEndedAction(
  playbackRequest: number
): EnglishRhythmPlayerAction {
  return { type: "model-ended", playbackRequest };
}

function createRhythmUnavailableAction(
  playbackRequest: number
): EnglishRhythmPlayerAction {
  return { type: "media-unavailable", playbackRequest };
}

function isPreviousKey(key: string): boolean {
  return key === "ArrowLeft" || key === "ArrowUp";
}

function isNextKey(key: string): boolean {
  return key === "ArrowRight" || key === "ArrowDown";
}

function focusControlIntoView(
  control: HTMLButtonElement | undefined
): void {
  if (control === undefined) {
    return;
  }

  control.focus({ preventScroll: true });
  control.scrollIntoView?.({
    behavior: "auto",
    block: "nearest",
    inline: "nearest"
  });
}

function getRhythmFocusDirection(
  key: string
): EnglishRhythmFocusDirection | null {
  if (key === "Home") {
    return "first";
  }

  if (key === "End") {
    return "last";
  }

  if (isPreviousKey(key)) {
    return "previous";
  }

  return isNextKey(key) ? "next" : null;
}

interface EnglishChoiceActivityProps {
  readonly audioPlayer?: CurriculumAudioPlayer;
  readonly bridge: EnglishActivityHostBridge<EnglishChoicePlayerAction>;
  readonly context: ActivityHostRenderContext<
    EnglishChoicePlayerAction,
    EnglishChoicePlayerRenderModel
  >;
}

export function EnglishChoiceActivity({
  audioPlayer = UNAVAILABLE_ENGLISH_AUDIO_PLAYER,
  bridge,
  context
}: EnglishChoiceActivityProps) {
  const { dispatch, getActivationProps, isPaused, model, reducedMotion } =
    context;
  const choiceRefs = useRef(new Map<string, HTMLButtonElement>());
  const [focusRequest, setFocusRequest] = useState(0);
  const [failedPictureAsset, setFailedPictureAsset] = useState<string | null>(
    null
  );
  const focusedChoiceId =
    model.choices.find(({ focused }) => focused)?.id ?? null;
  const prompt = model.prompt;
  const pictureAsset =
    prompt.kind === "picture-word-match" ? prompt.pictureAsset : null;
  const imageUnavailable =
    pictureAsset !== null && failedPictureAsset === pictureAsset;
  const feedbackAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    model.feedback,
    !isPaused
  );

  useActivityNarrationRequest({
    audioKey: prompt.audioKey,
    bridge,
    caption: prompt.audioCaption,
    createEndedAction: createChoiceEndedAction,
    createUnavailableAction: createChoiceUnavailableAction,
    inputMethod: prompt.audioInputMethod,
    paused: isPaused,
    playbackRequest: prompt.playbackRequest,
    shouldPlay: prompt.mediaStatus === "playing"
  });

  useEffect(() => {
    if (focusedChoiceId !== null) {
      focusControlIntoView(choiceRefs.current.get(focusedChoiceId));
    }
  }, [focusRequest, focusedChoiceId]);

  const handleChoiceKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentChoiceId: string) => {
      let direction: "previous" | "next" | null = null;

      if (isPreviousKey(event.key)) {
        direction = "previous";
      } else if (isNextKey(event.key)) {
        direction = "next";
      }

      if (direction !== null) {
        event.preventDefault();
        dispatch(
          { type: "move-focus", currentChoiceId, direction },
          "keyboard"
        );
        setFocusRequest((request) => request + 1);
      }
    },
    [dispatch]
  );

  const mediaFallback =
    prompt.mediaStatus === "unavailable" ? (
      <p
        aria-live="polite"
        className="english-player__media-fallback"
        role="status"
      >
        声音这次没出来。可以看着提示继续选，游戏不会停。
      </p>
    ) : null;
  const visibleCaption =
    prompt.kind === "letter-sound" ||
    prompt.mediaStatus === "playing" ||
    prompt.mediaStatus === "unavailable";

  return (
    <div
      aria-label="英文活动内容"
      className="english-player__viewport"
      role="region"
      tabIndex={0}
    >
      <section
        className="english-player"
        data-activity={prompt.kind}
        data-reduced-motion={reducedMotion}
      >
        <div className="english-player__prompt">
          {prompt.kind === "letter-sound" ? (
            <div className="english-player__sound-prompt">
              <p className="english-player__prompt-label">
                听一听这个字母音
              </p>
              <p
                aria-label={"国际音标 /" + prompt.phoneme + "/"}
                className="english-player__phoneme"
                lang="en"
              >
                /{prompt.phoneme}/
              </p>
              <p className="english-player__example">
                示例词：
                <span lang="en">{prompt.exampleWord}</span>
              </p>
            </div>
          ) : imageUnavailable ? (
            <div
              aria-label={prompt.pictureDescription}
              className="english-player__image-fallback"
              role="img"
            >
              <ImageOff aria-hidden="true" />
              <span>{prompt.pictureDescription}</span>
              <small>图片这次没有出现</small>
            </div>
          ) : (
            <img
              alt={prompt.pictureDescription}
              className="english-player__picture"
              onError={() => setFailedPictureAsset(prompt.pictureAsset)}
              src={prompt.pictureAsset}
            />
          )}

          <div className="english-player__media-controls">
            <AudioReplayButton
              disabled={isPaused}
              isPlaying={prompt.mediaStatus === "playing"}
              label={
                prompt.kind === "letter-sound"
                  ? "播放字母音"
                  : "听一听单词"
              }
              onClick={
                getActivationProps((inputMethod) => ({
                  type: "play-media",
                  inputMethod
                })).onClick
              }
              playingLabel="正在播放"
            />
            <p
              aria-live="polite"
              className="english-player__audio-caption"
              data-visible={visibleCaption}
              lang="en"
            >
              {visibleCaption ? prompt.audioCaption : "\u00a0"}
            </p>
          </div>
          {mediaFallback}
        </div>

        <div
          aria-label="英文答案选项"
          className="english-player__choices"
          role="group"
        >
          {model.choices.map((choice) => {
            const audioPath = choice.audioPath;

            return (
              <div
                className="english-player__choice-shell"
                data-has-voice={audioPath !== null}
                key={choice.id}
              >
                <button
                  aria-pressed={choice.selected}
                  className="english-player__choice"
                  data-focused={choice.focused}
                  disabled={choice.disabled || isPaused}
                  onKeyDown={(event) =>
                    handleChoiceKeyDown(event, choice.id)
                  }
                  ref={(element) => {
                    if (element === null) {
                      choiceRefs.current.delete(choice.id);
                    } else {
                      choiceRefs.current.set(choice.id, element);
                    }
                  }}
                  type="button"
                  {...getActivationProps((inputMethod) => ({
                    type: "choose",
                    choiceId: choice.id,
                    inputMethod
                  }))}
                >
                  <span lang="en">{choice.label}</span>
                </button>
                {audioPath === null ? null : (
                  <CurriculumVoiceButton
                    disabled={choice.disabled || isPaused}
                    isPlaying={audioPlayer.isPlayingPath(audioPath)}
                    label={choice.label}
                    onPlay={() => {
                      void audioPlayer.playPath(audioPath);
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="english-player__feedback-row">
          <div
            aria-live="polite"
            className="english-player__feedback"
            data-feedback={model.feedback?.kind ?? "none"}
            role="status"
          >
            {model.feedback?.message ?? "\u00a0"}
          </div>
          {model.feedback === null ? null : (
            <CurriculumVoiceButton
              disabled={isPaused}
              isPlaying={feedbackAudio.isPlaying}
              label={model.feedback.message}
              onPlay={feedbackAudio.replay}
            />
          )}
        </div>
      </section>
    </div>
  );
}

interface EnglishRhythmActivityProps {
  readonly audioPlayer?: CurriculumAudioPlayer;
  readonly bridge: EnglishActivityHostBridge<EnglishRhythmPlayerAction>;
  readonly context: ActivityHostRenderContext<
    EnglishRhythmPlayerAction,
    EnglishRhythmPlayerRenderModel
  >;
}

function getCueStatusText(status: string): string {
  switch (status) {
    case "current":
      return "现在";
    case "on-beat":
      return "跟上节奏";
    case "off-beat":
      return "顺序正确";
    case "wrong-order":
      return "再试一次";
    default:
      return "等待";
  }
}

export function EnglishRhythmActivity({
  audioPlayer = UNAVAILABLE_ENGLISH_AUDIO_PLAYER,
  bridge,
  context
}: EnglishRhythmActivityProps) {
  const { dispatch, getActivationProps, isPaused, model, reducedMotion } =
    context;
  const tokenRefs = useRef(new Map<number, HTMLButtonElement>());
  const [focusRequest, setFocusRequest] = useState(0);
  const focusedTokenIndex =
    model.tokens.find(({ focused }) => focused)?.index ?? null;
  const queuedFeedbackRef = useRef<{
    playbackRequest: number;
    started: boolean;
  } | null>(null);
  const feedbackAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    model.feedback,
    !isPaused && (model.feedback?.autoPlay ?? false)
  );
  const feedbackAudioPath = model.feedback?.audioPath ?? null;
  const feedbackPlaybackRequest = model.feedback?.playbackRequest ?? null;
  const queuedFeedbackIsPlaying =
    feedbackAudioPath !== null &&
    audioPlayer.isPlayingPath(feedbackAudioPath);
  const feedbackInputMethod = model.audio.inputMethod ?? "pointer";
  const { playPath, stopOwnedVoice } = audioPlayer;

  useEffect(() => {
    if (
      !model.audio.waitingForFeedback ||
      isPaused ||
      feedbackAudioPath === null ||
      feedbackPlaybackRequest === null
    ) {
      return;
    }

    const queued = {
      playbackRequest: feedbackPlaybackRequest,
      started: false
    };
    queuedFeedbackRef.current = queued;

    void playPath(feedbackAudioPath).then((started) => {
      if (queuedFeedbackRef.current !== queued) {
        return;
      }

      if (started) {
        queued.started = true;
        return;
      }

      queuedFeedbackRef.current = null;
      dispatch(
        {
          type: "feedback-audio-finished",
          playbackRequest: queued.playbackRequest
        },
        feedbackInputMethod
      );
    });

    return () => {
      if (queuedFeedbackRef.current === queued) {
        queuedFeedbackRef.current = null;
        stopOwnedVoice();
      }
    };
  }, [
    dispatch,
    feedbackAudioPath,
    feedbackInputMethod,
    feedbackPlaybackRequest,
    isPaused,
    model.audio.waitingForFeedback,
    playPath,
    stopOwnedVoice
  ]);

  useEffect(() => {
    const queued = queuedFeedbackRef.current;

    if (
      queued === null ||
      queued.playbackRequest !== feedbackPlaybackRequest
    ) {
      return;
    }

    if (queuedFeedbackIsPlaying) {
      queued.started = true;
      return;
    }

    if (!queued.started) {
      return;
    }

    queuedFeedbackRef.current = null;
    dispatch(
      {
        type: "feedback-audio-finished",
        playbackRequest: queued.playbackRequest
      },
      feedbackInputMethod
    );
  }, [
    dispatch,
    feedbackInputMethod,
    feedbackPlaybackRequest,
    queuedFeedbackIsPlaying
  ]);

  useActivityNarrationRequest({
    audioKey: model.audio.audioKey,
    bridge,
    caption: model.caption.text,
    createEndedAction: createRhythmEndedAction,
    createUnavailableAction: createRhythmUnavailableAction,
    inputMethod: model.audio.inputMethod,
    paused: isPaused,
    playbackRequest: model.audio.playbackRequest,
    shouldPlay:
      model.audio.shouldPlay && !model.audio.waitingForFeedback
  });

  useEffect(() => {
    if (focusedTokenIndex !== null) {
      focusControlIntoView(tokenRefs.current.get(focusedTokenIndex));
    }
  }, [focusRequest, focusedTokenIndex]);

  const handleTokenKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentTokenIndex: number) => {
      const direction = getRhythmFocusDirection(event.key);

      if (direction !== null) {
        event.preventDefault();
        dispatch(
          { type: "move-focus", currentTokenIndex, direction },
          "keyboard"
        );
        setFocusRequest((request) => request + 1);
      }
    },
    [dispatch]
  );

  return (
    <div
      aria-label="英文活动内容"
      className="english-player__viewport"
      role="region"
      tabIndex={0}
    >
      <section
        className="english-player english-player--rhythm"
        data-activity="rhythm-phrases"
        data-reduced-motion={reducedMotion}
      >
        <div className="english-player__rhythm-heading">
          <Music2 aria-hidden="true" />
          <div>
            <p className="english-player__prompt-label">
              听示范，再跟着节奏读
            </p>
            <p
              aria-live="polite"
              className="english-player__phrase-caption"
              data-synchronized={model.caption.synchronized}
              lang="en"
            >
              {model.caption.text}
            </p>
          </div>
          <AudioReplayButton
            disabled={isPaused}
            isPlaying={model.audio.shouldPlay}
            label={model.canStart ? "播放示范" : "重听示范"}
            onClick={
              getActivationProps((inputMethod) => ({
                type: model.canStart ? "start-model" : "replay-model",
                inputMethod
              })).onClick
            }
            playingLabel="正在播放示范"
          />
        </div>

        {model.caption.mediaFallback ? (
          <p
            aria-live="polite"
            className="english-player__media-fallback"
            role="status"
          >
            示范声音这次没出来。跟着英文字幕，也可以继续。
          </p>
        ) : null}

        <ol aria-label="节奏提示" className="english-player__cues">
          {model.cues.map((cue) => (
            <li
              className="english-player__cue"
              data-presentation={cue.presentation}
              data-status={cue.status}
              key={cue.index}
            >
              <span aria-hidden="true" className="english-player__beat" />
              <span lang="en">{cue.token}</span>
              <span className="english-player__cue-status">
                {getCueStatusText(cue.status)}
              </span>
            </li>
          ))}
        </ol>

        <div
          aria-label="按顺序选择英文词"
          className="english-player__tokens"
          role="group"
        >
          {model.tokens.map((token) => (
            <div className="english-player__token-shell" key={token.id}>
              <button
                className="english-player__token"
                data-focused={token.focused}
                disabled={token.disabled || isPaused}
                onKeyDown={(event) =>
                  handleTokenKeyDown(event, token.index)
                }
                ref={(element) => {
                  if (element === null) {
                    tokenRefs.current.delete(token.index);
                  } else {
                    tokenRefs.current.set(token.index, element);
                  }
                }}
                type="button"
                {...getActivationProps((inputMethod) => ({
                  type: "select-token",
                  token: token.token,
                  inputMethod
                }))}
              >
                <span lang="en">{token.token}</span>
              </button>
              <CurriculumVoiceButton
                disabled={token.disabled || isPaused}
                isPlaying={audioPlayer.isPlayingPath(token.audioPath)}
                label={token.token}
                onPlay={() => {
                  void audioPlayer.playPath(token.audioPath);
                }}
              />
            </div>
          ))}
        </div>

        <div className="english-player__feedback-row">
          <div
            aria-live="polite"
            className="english-player__feedback"
            data-feedback={model.feedback?.kind ?? "none"}
            role="status"
          >
            {model.feedback?.message ?? "\u00a0"}
          </div>
          {model.feedback === null ? null : (
            <CurriculumVoiceButton
              disabled={isPaused || model.audio.shouldPlay}
              isPlaying={feedbackAudio.isPlaying}
              label={model.feedback.message}
              onPlay={feedbackAudio.replay}
            />
          )}
        </div>
      </section>
    </div>
  );
}
